import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useTheme,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Typography,
  Button,
  Divider,
  SvgIcon,
  tooltipClasses,
} from "@mui/material";
import { useIntl } from "react-intl";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

import TitledCard from "../TitledCard";
import { IOPortValue, IOZoneValue, ZoneBehaviors } from "./types";
import useEditedStyles, { useEditedColor } from "../../../../hooks/useEditedStyles";
import { onNumberInputClick, uuidv4 } from "../../../../utils/utils";
import IOZone from "./IOZone";
import { portTypes } from "./SmartalarmBoard";
import InfoIcon from "@mui/icons-material/Info";
import { BootstrapTooltip } from "./PZConfigurator";
import { ResetAdornment, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";

interface IOPortProps {
  nr: number;
  value: IOPortValue;
  saved: IOPortValue | null;
  onChange: (nr: number, value: IOPortValue) => void;
  disabled?: boolean;
}

export const getDefaultIOZoneValue = () => {
  return { uuid: uuidv4(), enabled: true, name: null, eventAlarm: "", eventRecovery: "", zone: "", behavior: ZoneBehaviors.IMMEDIATE };
};

const IOPort = ({ nr, value, saved, disabled, onChange }: IOPortProps) => {
  const [type, setType] = useState("");
  const [resistor, setResistor] = useState("");
  const [zoneIndexes, setZoneIndexes] = useState<number[]>([]);
  const [resistorVisible, setResistorVisible] = useState(false);

  const theme = useTheme();
  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();
  const { background: editedBackground, text: editedText } = useEditedColor();
  const { openContextMenu } = useResetContextMenu();

  const onDelete = useCallback(() => {
    value.deleted = true;
    onChange(nr, value);
  }, [nr, value, onChange]);

  const onRestore = useCallback(() => {
    delete value.deleted;
    onChange(nr, value);
  }, [nr, value, onChange]);

  const handleTypeChange = useCallback(
    (type: string) => {
      if (!value) {
        return;
      }
      setType(type);
      const selectedType = portTypes.find((t) => t.value === type) || portTypes[0];
      value.type = selectedType.value;

      if (value.zoneValues === null && selectedType.value !== "NULL") {
        value.zoneValues = Array.from({ length: selectedType.value === "NULL" ? 0 : selectedType.double ? 2 : 1 }).map(() => getDefaultIOZoneValue());
      }
      setZoneIndexes(Array.from({ length: selectedType.value === "NULL" ? 0 : selectedType.double ? 2 : 1 }).map((_, i) => i));
      value.maxZones = selectedType.value === "NULL" ? 0 : selectedType.double ? 2 : 1;
      if (value.zoneValues.length < value.maxZones) {
        value.zoneValues = Array.from({ length: value.maxZones }).map((_, i) => value.zoneValues[i] || getDefaultIOZoneValue());
      }
      setResistorVisible(selectedType.r || false);

      onChange(nr, value);
    },
    [nr, value, onChange]
  );

  const handleZoneValueChange = useCallback(
    (index: number, v: IOZoneValue) => {
      if (!value?.zoneValues) {
        return;
      }
      value.zoneValues[index] = v;
      onChange(nr, value);
    },
    [nr, value, onChange]
  );

  const resetValue = useCallback(
    (key: "type" | "resistor", input?: HTMLInputElement) => {
      if (!saved) {
        return;
      }
      console.log("input value", key, input?.value);
      switch (key) {
        case "type":
          value.type = saved.type;
          break;
        case "resistor":
          value.resistor = saved.resistor;
          if (input) {
            console.log("input value", input?.value);
            input.value = `${value.resistor}`;
          }
          break;
      }
      onChange(nr, value);
    },
    [nr, onChange, saved, value]
  );

  useEffect(() => {
    if (!value) {
      return;
    }
    const selectedType = portTypes.find((t) => t.value === value.type) || portTypes[0];
    setZoneIndexes(Array.from({ length: selectedType.value === "NULL" ? 0 : selectedType.double ? 2 : 1 }).map((_, i) => i));
    setType(selectedType.value);
    setResistor(value.resistor ? value.resistor.toString(10) : "");
    setResistorVisible(selectedType.r || false);
  }, [nr, value]);

  const zones = useMemo(() => {
    if (!value) {
      return null;
    }
    return zoneIndexes.map((_, index) => (
      <Grid item flex={1} key={`zone-${nr}-${value.zoneValues[index]?.uuid || `new-${index}`}`}>
        <Divider />
        <IOZone
          index={index}
          type={value.type}
          value={value.zoneValues[index]}
          saved={saved?.zoneValues?.[index] || null}
          onChange={handleZoneValueChange}
          disabled={disabled || value.deleted}
        />
      </Grid>
    ));
  }, [value, saved, zoneIndexes, nr, disabled, handleZoneValueChange]);

  if (!value) {
    return null;
  }

  const selectedType = portTypes.find((pt) => pt.value === type);

  const typeSelectAdornment =
    type !== "OUT" || (saved && saved.type !== type) ? (
      <>
        {type !== "OUT" && (
          <BootstrapTooltip
            backgroundcolor={theme.palette.common.white}
            sx={{
              [`& .${tooltipClasses.tooltip}`]: {
                border: `1px solid ${theme.palette.divider}`,
              },
            }}
            title={
              selectedType?.image ? (
                <img src={selectedType.image} style={{ height: 65, width: 250, maxWidth: "100%", margin: "auto" }} alt={selectedType.value} />
              ) : (
                ""
              )
            }
          >
            <SvgIcon sx={{ mr: 4, position: "absolute", padding: 0, right: "0", top: "50%", transform: "translateY(-50%)" }} fontSize="small">
              <InfoIcon />
            </SvgIcon>
          </BootstrapTooltip>
        )}
        {saved && saved.type !== type && <ResetAdornment sx={{ mr: type !== "OUT" ? 6 : 2 }} />}
      </>
    ) : undefined;

  return (
    <TitledCard
      title={
        (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") + formatMessage({ defaultMessage: "IO port #{nr}" }, { nr })
      }
      postfix={
        !disabled &&
        (value.deleted ? (
          <Button color="inherit" size="small" onClick={onRestore} sx={{ mx: 1, py: 0 }}>
            {formatMessage({ defaultMessage: "Restore" })}
          </Button>
        ) : (
          <IconButton onClick={onDelete} sx={{ mx: 1, p: 0 }}>
            <DeleteForeverOutlinedIcon />
          </IconButton>
        ))
      }
      outlined
      color={value.deleted ? editedBackground : theme.palette.secondary.main}
      contrastColor={value.deleted ? editedText : theme.palette.secondary.contrastText}
    >
      <Grid container spacing={1} flexDirection="column" sx={{ padding: 1 }}>
        <Grid item container spacing={1} flexDirection="row" alignItems={"center"}>
          <Grid item xs={12} sm={6} md="auto">
            <FormControl
              variant="filled"
              size="small"
              color="secondary"
              sx={{ margin: 0, minWidth: 200 }}
              className={saved?.type !== type ? editedStyle.input : ""}
              disabled={disabled || value.deleted}
              fullWidth
              onContextMenu={
                saved && saved.type !== type
                  ? (e) =>
                      openContextMenu(e, "", {
                        onReset: () => resetValue("type"),
                      })
                  : undefined
              }
            >
              <InputLabel id="select-label">{formatMessage({ defaultMessage: "IO port mode" })}</InputLabel>
              <Select
                labelId="select-label"
                value={type}
                onChange={(e) => handleTypeChange(e.target.value)}
                endAdornment={typeSelectAdornment}
                renderValue={() => {
                  return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      {selectedType?.label}
                      <SvgIcon sx={{ visibility: "hidden", ml: 1 }} fontSize="small" color="primary">
                        <InfoIcon />
                      </SvgIcon>
                    </div>
                  );
                }}
              >
                {portTypes.map((option) => (
                  <MenuItem key={`option-${option.value}`} value={option.value}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      {option.label}
                      {option.image && <img src={option.image} style={{ height: 65, width: 250, maxWidth: "100%", margin: "auto" }} alt={option.value} />}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {resistorVisible && (
            <Grid item xs={12} sm={6} md="auto">
              <TextField
                label={formatMessage({ defaultMessage: "Resistor value" })}
                variant="filled"
                value={resistor}
                className={saved?.resistor !== parseFloat(resistor) ? editedStyle.input : ""}
                inputProps={{ min: 0, step: 1, type: "number", onClick: onNumberInputClick }}
                onChange={(e) => setResistor(e.target.value)}
                onBlur={() => {
                  value.resistor = parseFloat(resistor);
                  onChange(nr, value);
                }}
                InputProps={{
                  endAdornment: (
                    <>
                      {saved && saved.resistor !== (resistor ? parseInt(resistor, 10) : null) ? <ResetAdornment /> : null}
                      <InputAdornment position="end">
                        <Typography variant="body2">&Omega;</Typography>
                      </InputAdornment>
                    </>
                  ),
                }}
                sx={{ margin: 0, md: { minWidth: 300, width: 300 } }}
                size="small"
                color="secondary"
                disabled={disabled || value.deleted}
                fullWidth
                onContextMenu={
                  saved && saved.resistor !== (resistor ? parseInt(resistor, 10) : null)
                    ? (e) =>
                        openContextMenu(e, "", {
                          onReset: (input) => resetValue("resistor", input),
                        })
                    : undefined
                }
              />
            </Grid>
          )}
        </Grid>
        <Grid item flex={1} container spacing={1} flexDirection="column">
          {zones}
        </Grid>
      </Grid>
    </TitledCard>
  );
};

export default IOPort;
